import React, { useEffect, useState, useRef } from 'react';
import { Link } from 'react-router-dom';
import { useAuthRedirect } from '../utils/authRedirect';
import { useUser } from '../context/UserContext';
import ConfirmationModal from './ConfirmationModal';
import AdSenseAd from './AdSenseAd';


function SavedRecipes() {
    const [recipes, setRecipes] = useState([]);
    const searchRef = useRef(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [allTags, setAllTags] = useState([]);
    const [selectedTag, setSelectedTag] = useState(null);
    const [searchTerm, setSearchTerm] = useState('');
    const [showModal, setShowModal] = useState(false);
    const [recipeToDelete, setRecipeToDelete] = useState(null);

    const handleAuthError = useAuthRedirect();
    const { user, loading: userLoading } = useUser();
    const shouldShowAds = !userLoading && user?.hasNoAds === false;

    useEffect(() => {
        if (searchRef && searchRef.current) searchRef.current.focus();

        const fetchRecipesAndTags = async () => {
            setLoading(true);
            try {
                const token = localStorage.getItem('token');

                const recipeResponse = await fetch('/api/recipes', {
                    headers: { 'x-auth-token': token },
                });

                if (handleAuthError(recipeResponse.status)) {
                    return;
                }

                if (!recipeResponse.ok) {
                    const errData = await recipeResponse.json();
                    throw new Error(errData.error || 'Something went wrong while fetching recipes.');
                }

                const recipeData = await recipeResponse.json();
                setRecipes(recipeData);

                const tagsResponse = await fetch('/api/recipes/tags', {
                    headers: { 'x-auth-token': token },
                });

                if (handleAuthError(tagsResponse.status)) {
                    return;
                }

                if (!tagsResponse.ok) {
                    const errData = await tagsResponse.json();
                    throw new Error(errData.error || 'Something went wrong while fetching tags.');
                }

                const tagsData = await tagsResponse.json();
                setAllTags(tagsData);
            } catch (err) {
                setError(err.message);
            } finally {
                setLoading(false);
            }
        };

        fetchRecipesAndTags();
    }, [handleAuthError]);

    const confirmDelete = (id) => {
        setRecipeToDelete(id);
        setShowModal(true);
    };

    const cancelDelete = () => {
        setShowModal(false);
        setRecipeToDelete(null);
    };

    const executeDelete = async () => {
        setShowModal(false);
        if (!recipeToDelete) return;

        try {
            const token = localStorage.getItem('token');
            const response = await fetch(`/api/recipes/${recipeToDelete}`, {
                method: 'DELETE',
                headers: { 'x-auth-token': token },
            });

            if (handleAuthError(response.status)) {
                return;
            }

            if (!response.ok) {
                const errData = await response.json();
                throw new Error(errData.error || 'Something went wrong while deleting the recipe.');
            }

            setRecipes(recipes.filter((recipe) => recipe._id !== recipeToDelete));

            const tokenAfterDelete = localStorage.getItem('token');
            const tagsResponse = await fetch('/api/recipes/tags', {
                headers: { 'x-auth-token': tokenAfterDelete },
            });
            if (tagsResponse.ok) {
                const tagsData = await tagsResponse.json();
                setAllTags(tagsData);
            }
        } catch (err) {
            setError(err.message);
        } finally {
            setRecipeToDelete(null);
        }
    };

    const filteredRecipes = recipes.filter((recipe) => {
        const matchesSearch = recipe.title.toLowerCase().includes(searchTerm.toLowerCase());
        const matchesTag = selectedTag ? recipe.tags.includes(selectedTag) : true;
        return matchesSearch && matchesTag;
    });

    return (
        <div className="page saved-recipes-page">
            <header className="page-header">
                <span className="page-eyebrow">Saved recipes</span>
                <h1>Your cookbook</h1>
                <p className="page-subtitle">
                    Search and organize the recipes you have captured with RecipeCore.
                </p>
            </header>

            {shouldShowAds && (
                <AdSenseAd className="ad-slot--inline mb-4" minHeight={250} />
            )}

            <section className="panel saved-recipes-toolbar">
                <div className="profile-search">
                    <div>
                        <h2>Find a recipe</h2>
                        <p className="text-muted">Search by title and refine with tags to get to what you need quickly.</p>
                    </div>
                    <input
                        ref={searchRef}
                        aria-label="Search saved recipes"
                        type="text"
                        className="form-control"
                        placeholder="Search your recipes..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                    />
                    <div aria-live="polite" className="sr-only">{filteredRecipes.length} results</div>
                </div>

                <div className="mt-4">
                    <div className="d-flex justify-content-between align-items-center gap-3 flex-wrap">
                        <h5 className="mb-0">Filter by tags</h5>
                        {(searchTerm || selectedTag) && (
                            <button
                                type="button"
                                className="btn btn-link p-0"
                                aria-label="Clear search filters"
                                onClick={() => {
                                    setSearchTerm('');
                                    setSelectedTag(null);
                                }}
                            >
                                Clear filters
                            </button>
                        )}
                    </div>
                    <ul className="list-group mt-2">
                        <li
                            className={`list-group-item ${selectedTag === null ? 'active' : ''}`}
                            onClick={() => setSelectedTag(null)}
                            role="button"
                            tabIndex="0"
                            onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setSelectedTag(null); } }}
                        >
                            All recipes
                        </li>
                        {allTags.map((tag) => (
                            <li
                                key={tag}
                                className={`list-group-item ${selectedTag === tag ? 'active' : ''}`}
                                onClick={() => setSelectedTag(tag)}
                                role="button"
                                tabIndex="0"
                                onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setSelectedTag(tag); } }}
                            >
                                {tag}
                            </li>
                        ))}
                    </ul>
                </div>
            </section>

            {loading ? (
                <p>Loading recipes...</p>
            ) : error ? (
                <div className="alert alert-danger" role="alert">{error}</div>
            ) : (
                <section className="profile-recipes mt-4">
                    <h2>Your saved recipes</h2>
                    {filteredRecipes.length === 0 ? (
                        <div className="empty-state">
                            {searchTerm || selectedTag ? 'No recipes match your filters yet.' : 'You have not saved any recipes yet.'}
                        </div>
                    ) : (
                        <div className="profile-recipes-grid">
                            {filteredRecipes.map((recipe) => (
                                <article
                                    key={recipe._id}
                                    className="card recipe-card"
                                    tabIndex={0}
                                    onKeyDown={(e) => { if (e.key === 'Enter') { window.location.href = `/recipes/${recipe._id}`; } }}
                                >
                                    <div className="card-body">
                                        <h5>{recipe.title}</h5>
                                        {recipe.tags && recipe.tags.length > 0 && (
                                            <div className="saved-recipe-meta mt-3">
                                                {recipe.tags.map((tag) => (
                                                    <span key={tag} className="tag-pill">{tag}</span>
                                                ))}
                                            </div>
                                        )}
                                        <div className="recipe-actions mt-4">
                                            <Link to={`/recipes/${recipe._id}`} className="btn btn-primary btn-sm me-2">
                                                View details
                                            </Link>
                                            {recipe.url && (
                                                <a
                                                    href={recipe.url}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                    className="btn btn-secondary btn-sm"
                                                >
                                                    Original source
                                                </a>
                                            )}
                                        </div>
                                    </div>
                                    <div className="card-footer d-flex justify-content-between align-items-center">
                                        <span className="text-muted" style={{ fontSize: '0.85rem' }}>Saved recipe</span>
                                        <button onClick={() => confirmDelete(recipe._id)} aria-label={`Delete recipe ${recipe.title}`} className="btn btn-danger btn-sm">
                                            Delete
                                        </button>
                                    </div>
                                </article>
                            ))}
                        </div>
                    )}
                </section>
            )}

            <ConfirmationModal
                show={showModal}
                title="Confirm Deletion"
                message="Are you sure you want to delete this recipe? This action cannot be undone."
                onConfirm={executeDelete}
                onCancel={cancelDelete}
            />

            {/* Helpful empty-state action */}
            {(!loading && !error && filteredRecipes.length === 0) && !searchTerm && (
                <div className="mt-3">
                    <p className="text-muted">Want to try extracting a recipe? Use the workspace to paste a recipe URL or text.</p>
                    <Link to="/app" className="btn btn-primary">Open extractor</Link>
                </div>
            )}
        </div>
    );
}

export default SavedRecipes;
